export const LANG_DB_DEFAULT_VERSION = 1;
export const LANG_DB_NAME = 'vistrian_local_lang_db';

// Object store names
export const LANG_LIST_STORE = 'TBL_LANGUAGE_LIST';
export const LANG_DETAILS_STORE = 'TBL_LANGUAGE_DETAILS';

// Index names
export const LANG_KEY_INDEX = 'langKey';
export const TEXT_LABEL_INDEX = 'textLabel';

// Singleton database connection
let dbConnection = null;

// Cache for translations and language list
let translationCache = {};
let languageListCache = [];
let userPrefferedLanguage = '';

export const openLangDB = async () => {
  try {
    const db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(LANG_DB_NAME, LANG_DB_DEFAULT_VERSION);

      request.onerror = (event) => {
        console.log('Error opening IndexedDB:', event.target.log);
        reject(event.target.log);
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        // console.log('Successfully opened IndexedDB');
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        console.log('Database upgrade needed');
        const db = event.target.result;

        // Create TBL_LANGUAGE_LIST object store
        const langListStore = db.createObjectStore(LANG_LIST_STORE, {
          keyPath: 'langId',
          autoIncrement: true
        });
        // Create indexes
        langListStore.createIndex(LANG_KEY_INDEX, 'langKey', { unique: true });
        langListStore.createIndex('versionKey', 'versionKey', {
          unique: false
        });

        // Create TBL_LANGUAGE_DETAILS object store
        const langDetailsStore = db.createObjectStore(LANG_DETAILS_STORE, {
          keyPath: 'langDetailsId',
          autoIncrement: true
        });
        // Create indexes
        langDetailsStore.createIndex(LANG_KEY_INDEX, 'langKey', {
          unique: false
        });
        langDetailsStore.createIndex(TEXT_LABEL_INDEX, 'textLabel', {
          unique: false
        });
        langDetailsStore.createIndex('textValue', 'textValue', {
          unique: false
        });

        console.log('Database tables created successfully');
      };
    });
    return db;
  } catch (error) {
    console.log('Error in openLangDB:', error);
    throw error;
  }
};

// Function to get or create database connection
const getDBConnection = async () => {
  if (!dbConnection) {
    dbConnection = await openLangDB();
  }
  return dbConnection;
};

// Function to close database connection
export const closeDBConnection = () => {
  if (dbConnection) {
    dbConnection.close();
    dbConnection = null;
  }
};

// Function to delete existing database
export const deleteLangDB = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(LANG_DB_NAME);

    request.onerror = (event) => {
      console.log('Error deleting database:', event.target.log);
      reject(event.target.log);
    };

    request.onsuccess = (event) => {
      console.log('Database deleted successfully');
      resolve();
    };
  });

// Function to initialize database
export const initLangDB = async () => {
  try {
    // First delete existing database
    await deleteLangDB();
    // Then open new database which will trigger onupgradeneeded
    const db = await openLangDB();
    return db;
  } catch (error) {
    console.log('Error initializing database:', error);
    throw error;
  }
};

// Get language list
export const getLanguageList = async (langKey) => {
  let db = null;
  try {
    db = await openLangDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(LANG_LIST_STORE, 'readonly');
      const objectStore = transaction.objectStore(LANG_LIST_STORE);
      const index = objectStore.index(LANG_KEY_INDEX);

      const request = langKey ? index.getAll(langKey) : objectStore.getAll();

      request.onsuccess = (event) => {
        const languages = event.target.result.map((lang) => ({
          ...lang,
          versionKey: lang.versionKey ?? 0
        }));
        resolve(languages);
      };

      request.onerror = (event) => {
        console.log('Error getting language list:', event.target.log);
        reject(event.target.log);
      };

      // Handle transaction completion
      transaction.oncomplete = () => {
        db.close();
      };

      transaction.onerror = (event) => {
        console.log('Transaction error:', event.target.log);
        db.close();
        reject(event.target.log);
      };
    });
  } catch (error) {
    if (db) db.close();
    console.log('Error in getLanguageList:', error);
    throw error;
  }
};

// Get language details by langKey
export const getLanguageDetails = async (langKey) => {
  let db = null;
  try {
    db = await openLangDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(LANG_DETAILS_STORE, 'readonly');
      const objectStore = transaction.objectStore(LANG_DETAILS_STORE);
      const index = objectStore.index(LANG_KEY_INDEX);

      const request = index.getAll(langKey);

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.log('Error getting language details:', event.target.log);
        reject(event.target.log);
      };

      // Handle transaction completion
      transaction.oncomplete = () => {
        db.close();
      };

      transaction.onerror = (event) => {
        console.log('Transaction error:', event.target.log);
        db.close();
        reject(event.target.log);
      };
    });
  } catch (error) {
    if (db) db.close();
    console.log('Error in getLanguageDetails:', error);
    throw error;
  }
};

// Add new language
export const addLanguage = async (languageData) => {
  let db = null;
  try {
    db = await openLangDB();
    const transaction = db.transaction(LANG_LIST_STORE, 'readwrite');
    const objectStore = transaction.objectStore(LANG_LIST_STORE);
    const index = objectStore.index(LANG_KEY_INDEX);

    // First check if language exists
    const existingLang = await new Promise((resolve, reject) => {
      const request = index.getAll(languageData.langKey);

      request.onsuccess = (event) => {
        resolve(event.target.result[0]); // Get first matching record
      };

      request.onerror = (event) => {
        console.log('Error checking existing language:', event.target.log);
        reject(event.target.log);
      };
    });

    if (existingLang) {
      // Update existing language
      const updateRequest = objectStore.put({
        ...existingLang,
        versionKey: languageData.versionKey || existingLang.versionKey
      });

      return new Promise((resolve, reject) => {
        updateRequest.onsuccess = (event) => {
          console.log('Language updated successfully');
          resolve(event.target.result);
        };

        updateRequest.onerror = (event) => {
          console.log('Error updating language:', event.target.log);
          reject(event.target.log);
        };
      });
    }

    // Add new language
    const addRequest = objectStore.add(languageData);

    return new Promise((resolve, reject) => {
      addRequest.onsuccess = (event) => {
        console.log('New language added successfully');
        resolve(event.target.result);
      };

      addRequest.onerror = (event) => {
        console.log('Error adding language:', event.target.log);
        reject(event.target.log);
      };
    });
  } catch (error) {
    console.log('Error in addLanguage:', error);
    throw error;
  } finally {
    if (db) db.close();
  }
};

// Add or update language details
export const addLanguageDetails = async (translations) => {
  let db = null;
  try {
    db = await openLangDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(LANG_DETAILS_STORE, 'readwrite');
      const objectStore = transaction.objectStore(LANG_DETAILS_STORE);
      const index = objectStore.index(TEXT_LABEL_INDEX);

      // Ensure translations is an array
      const translationsArray = Array.isArray(translations)
        ? translations
        : [translations];

      // Process each translation
      const promises = translationsArray.map(
        (details) =>
          new Promise((resolveItem, rejectItem) => {
            // Validate required fields
            if (!details.langKey || !details.textLabel) {
              console.log(details);
            }

            // First check if the translation exists
            const getRequest = index.getAll(details.textLabel);

            getRequest.onsuccess = (event) => {
              const existingTranslations = event.target.result;
              const existingTranslation = existingTranslations.find(
                (item) =>
                  item.langKey === details.langKey &&
                  item.textLabel === details.textLabel
              );

              if (existingTranslation) {
                // Update existing translation
                const updateRequest = objectStore.put({
                  ...existingTranslation,
                  textValue: details.textValue
                });

                updateRequest.onsuccess = () => {
                  resolveItem(existingTranslation.langDetailsId);
                };

                updateRequest.onerror = (event) => {
                  console.log('Error updating translation:', event.target.log);
                  rejectItem(event.target.log);
                };
              } else {
                // Add new translation
                const addRequest = objectStore.add({
                  langKey: details.langKey,
                  textLabel: details.textLabel,
                  textValue: details.textValue
                });

                addRequest.onsuccess = (event) => {
                  resolveItem(event.target.result);
                };

                addRequest.onerror = (event) => {
                  rejectItem(event.target.log);
                };
              }
            };

            getRequest.onerror = (event) => {
              console.log(
                'Error checking existing translation:',
                event.target.log
              );
              rejectItem(event.target.log);
            };
          })
      );

      // Handle all translations
      Promise.all(promises)
        .then((results) => {
          resolve(results);
        })
        .catch((error) => {
          reject(error);
        });

      // Handle transaction completion
      transaction.oncomplete = () => {
        db.close();
      };

      transaction.onerror = (event) => {
        console.log('Transaction error:', event.target.log);
        db.close();
        reject(event.target.log);
      };
    });
  } catch (error) {
    if (db) db.close();
    console.log('Error in addLanguageDetails:', error);
    throw error;
  }
};

// Example usage:
// Single translation
// await addLanguageDetails({
//   langKey: 'en',
//   textLabel: 'welcome',
//   textValue: 'Welcome'
// });

// Multiple translations
// await addLanguageDetails([
//   {
//     langKey: 'en',
//     textLabel: 'welcome',
//     textValue: 'Welcome'
//   },
//   {
//     langKey: 'es',
//     textLabel: 'welcome',
//     textValue: 'Bienvenido'
//   }
// ]);

// Initialize the cache
const initializeTranslationCache = async () => {
  try {
    const db = await getDBConnection();

    // First get language list
    const langTransaction = db.transaction(LANG_LIST_STORE, 'readonly');
    const langObjectStore = langTransaction.objectStore(LANG_LIST_STORE);
    const langRequest = langObjectStore.getAll();

    langRequest.onsuccess = (event) => {
      languageListCache = event.target.result || [];
    };

    // Then get translations
    const transTransaction = db.transaction(LANG_DETAILS_STORE, 'readonly');
    const transObjectStore = transTransaction.objectStore(LANG_DETAILS_STORE);
    const transRequest = transObjectStore.getAll();

    transRequest.onsuccess = (event) => {
      const translations = event.target.result;
      translations.forEach((translation) => {
        const key = `${translation.langKey}_${translation.textLabel}`;
        translationCache[key] = translation.textValue;
      });
    };
  } catch (error) {
    console.log('Error initializing translation cache:', error);
  }
};

// Call this when your app starts
initializeTranslationCache();

export const getStringLocale = (stringInput, lang = 'HINDI') => {
  // Check if language exists in the cache

  const langExists = languageListCache.some(
    (l) => l.langKey === userPrefferedLanguage
  );
  if (!langExists) {
    return stringInput;
  }

  const cacheKey = `${userPrefferedLanguage}_${stringInput}`;
  return translationCache[cacheKey] || stringInput;
};

// Function to update cache when new translations are added
export const updateTranslationCache = async (langKey) => {
  translationCache = {}; // Clear existing cache
  languageListCache = []; // Clear language list cache
  userPrefferedLanguage = langKey;
  await initializeTranslationCache();
};
